import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { TenantsAPI, InstitutionsAPI, CampusesAPI } from "@/lib/sprint2-api";
import { PortalSlugEditor } from "@/components/PortalSlugEditor";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Loader2, Pencil, Trash2, MapPin, Mail, Phone, Image as ImageIcon } from "lucide-react";
import BrandingDialog from "@/components/branding/BrandingDialog";

export default function Campuses() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const focusId = searchParams.get("id");
  const focusRef = useRef(null);

  const [tenantId, setTenantId] = useState("");
  const [institutionId, setInstitutionId] = useState("all");
  const [editing, setEditing] = useState(null);
  const [branding, setBranding] = useState(null);

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");
  const tenantParam = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });

  const campusesQuery = useQuery({
    queryKey: ["campuses", effectiveTenantId, institutionId],
    queryFn: () => CampusesAPI.list({
      ...(institutionId !== "all" ? { institution_id: institutionId } : {}),
      ...(tenantParam || {}),
    }),
    enabled: !!effectiveTenantId,
  });
  const statsQuery = useQuery({
    queryKey: ["campus-stats", effectiveTenantId, institutionId],
    queryFn: () => CampusesAPI.stats({
      ...(institutionId !== "all" ? { institution_id: institutionId } : {}),
      ...(tenantParam || {}),
    }),
    enabled: !!effectiveTenantId,
  });
  const statsById = new Map((statsQuery.data || []).map((s) => [s.campus_id, s]));

  // Auto-scroll the focused campus into view when arrived via deep link.
  useEffect(() => {
    if (focusId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusId, campusesQuery.data]);

  const remove = async (campus) => {
    if (!window.confirm(`Delete campus "${campus.name}"?`)) return;
    try {
      await CampusesAPI.remove(campus.id);
      toast.success("Campus removed");
      if (focusId === campus.id) setSearchParams({});
      queryClient.invalidateQueries({ queryKey: ["campuses"] });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const institutionName = (iid) => (institutionsQuery.data || []).find((i) => i.id === iid)?.name || "—";

  return (
    <AppShell>
      <div className="px-6 py-5" data-testid="campuses-page">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h1 className="font-display font-bold tracking-tight text-2xl">Campuses</h1>
            <p className="text-[12px] text-muted-foreground/80 mt-0.5">Every campus across all institutions in this tenant.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-5 p-3 bg-card rounded-2xl shadow-sm">
          {isSuper && (
            <div className="space-y-1 min-w-[180px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Tenant</Label>
              <Select value={effectiveTenantId} onValueChange={(v) => { setTenantId(v); setInstitutionId("all"); }}>
                <SelectTrigger data-testid="campuses-scope-tenant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tenantsQuery.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1 min-w-[220px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Institution</Label>
            <Select value={institutionId} onValueChange={setInstitutionId}>
              <SelectTrigger data-testid="campuses-scope-institution"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All institutions</SelectItem>
                {institutionsQuery.data?.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {campusesQuery.isLoading ? (
          <div className="text-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
        ) : !(campusesQuery.data || []).length ? (
          <div className="bg-card rounded-2xl shadow-sm p-10 text-center text-sm text-muted-foreground">No campuses to show.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="campuses-grid">
            {(campusesQuery.data || []).map((c) => {
              const focused = focusId === c.id;
              const s = statsById.get(c.id);
              return (
                <div
                  key={c.id}
                  ref={focused ? focusRef : null}
                  className={`border rounded-md bg-card p-4 transition group cursor-pointer ${focused ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-primary/40 hover:shadow-md"}`}
                  data-testid={`campus-card-${c.id}`}
                  onClick={() => navigate(`/campuses/${c.id}`)}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-4 w-4 text-primary shrink-0" />
                      <div className="font-display font-semibold truncate group-hover:text-primary transition">{c.name}</div>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(c)} data-testid={`campus-edit-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setBranding(c)} title="Branding" data-testid={`campus-brand-${c.id}`}>
                        <ImageIcon className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(c)} data-testid={`campus-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] mb-2">{institutionName(c.institution_id)}</Badge>
                  <div className="mb-2">
                    <PortalSlugEditor
                      kind="campus"
                      id={c.id}
                      value={c.slug}
                      onSaved={() => queryClient.invalidateQueries({ queryKey: ["campuses"] })}
                    />
                  </div>
                  {s && (
                    <div className="flex gap-3 mb-2 mt-1 text-[11px]" data-testid={`campus-stats-${c.id}`}>
                      <Stat label="Students" value={s.students} />
                      <Stat label="Classes" value={s.classes} />
                      <Stat label="Sections" value={s.sections} />
                    </div>
                  )}
                  <div className="space-y-1 text-[12px] text-muted-foreground">
                    {c.address && <div className="flex items-start gap-1.5"><MapPin className="h-3 w-3 mt-0.5 shrink-0" /><span className="truncate">{[c.address, c.city, c.state].filter(Boolean).join(", ")}</span></div>}
                    {c.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{c.email}</span></div>}
                    {c.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 shrink-0" /><span>{c.phone}</span></div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {editing && <EditDialog campus={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); queryClient.invalidateQueries({ queryKey: ["campuses"] }); }} />}
        {branding && (
          <BrandingDialog
            kind="campuses"
            id={branding.id}
            name={branding.name}
            current={{ logo_url: branding.logo_url, background_url: branding.background_url }}
            onClose={() => setBranding(null)}
          />
        )}
      </div>
    </AppShell>
  );
}

function EditDialog({ campus, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: campus.name || "",
    address: campus.address || "",
    city: campus.city || "",
    state: campus.state || "",
    phone: campus.phone || "",
    email: campus.email || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await CampusesAPI.update(campus.id, form);
      toast.success("Campus updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="campus-edit-dialog">
        <DialogHeader><DialogTitle>Edit campus</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="campus-edit-name" /></Field>
          <Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="campus-edit-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex-1 border border-border/60 rounded px-2 py-1 bg-muted/30">
      <div className="font-display font-bold tabular-nums text-sm leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
